import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths, isSameMonth, isToday } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/tableUtils";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Jadwal() {
  const [month, setMonth] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDay, setDetailDay] = useState(null);
  const [confirmSched, setConfirmSched] = useState(null);
  const [form, setForm] = useState({ ruangan: "", tanggal: "", deskripsi: "" });

  const load = () => {
    api.get("/schedules", { params: { month: month.getMonth() + 1, year: month.getFullYear() } })
      .then((r) => setSchedules(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(load, [month]);
  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/rooms").then((r) => setDbRooms(r.data.map((x) => x.nama))).catch(() => {});
  }, []);

  const [dbRooms, setDbRooms] = useState([]);

  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  }), [month]);

  const byDate = useMemo(() => {
    const map = {};
    schedules.forEach((s) => {
      (map[s.tanggal] = map[s.tanggal] || []).push(s);
    });
    return map;
  }, [schedules]);

  const openAdd = (day) => {
    setForm({ ruangan: "", tanggal: format(day, "yyyy-MM-dd"), deskripsi: "" });
    setDialogOpen(true);
  };

  const rooms = [...new Set([...dbRooms, ...assets.map((a) => a.ruangan).filter(Boolean)])];

  const save = async () => {
    if (!form.ruangan || !form.tanggal) {
      toast.error("Ruangan dan tanggal wajib diisi");
      return;
    }
    try {
      await api.post("/schedules", form);
      toast.success("Jadwal ditambahkan");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const markDone = async (s) => {
    try {
      await api.patch(`/schedules/${s.id}`, { status: s.status === "Terealisasi" ? "Belum Terealisasi" : "Terealisasi" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/schedules/${s.id}`);
      toast.success("Jadwal dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="jadwal-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Jadwal Maintenance</h1>
          <p className="text-sm text-muted-foreground">Kalender jadwal pemeliharaan per ruangan.</p>
        </div>
        <Button onClick={() => openAdd(new Date())} data-testid="tambah-jadwal-button">
          <Plus className="w-4 h-4 mr-2" /> Tambah Jadwal
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <Button variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, -1))} data-testid="prev-month-button">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="font-heading font-semibold text-lg capitalize" data-testid="calendar-month-label">
            {format(month, "MMMM yyyy", { locale: localeId })}
          </h2>
          <Button variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="next-month-button">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">
          {["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"].map((d) => (
            <div key={d} className="py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const items = byDate[key] || [];
            return (
              <button
                key={key}
                onClick={() => ((byDate[key] || []).length > 0 ? setDetailDay(key) : openAdd(day))}
                data-testid={`calendar-day-${key}`}
                className={`min-h-[96px] border-b border-r border-border p-2 text-left align-top transition-colors duration-200 hover:bg-accent/60 ${
                  isSameMonth(day, month) ? "" : "bg-muted/40 text-muted-foreground"
                }`}
              >
                <span className={`inline-flex w-6 h-6 items-center justify-center text-xs rounded-full ${isToday(day) ? "bg-primary text-primary-foreground font-bold" : ""}`}>
                  {format(day, "d")}
                </span>
                <div className="mt-1 space-y-1">
                  {items.slice(0, 2).map((s) => (
                    <div key={s.id} className={`text-[11px] leading-tight px-1.5 py-0.5 rounded truncate ${
                      s.status === "Terealisasi" ? "bg-emerald-100 text-emerald-800 line-through" : "bg-primary/10 text-primary"
                    }`}>
                      {s.ruangan} · {s.deskripsi || "Maintenance"}
                    </div>
                  ))}
                  {items.length > 2 && <p className="text-[10px] text-muted-foreground">+{items.length - 2} lainnya</p>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-6">
        <h2 className="font-heading font-semibold text-lg mb-4">Daftar Jadwal Bulan Ini</h2>
        <div className="space-y-2">
          {schedules.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 border border-border rounded-md px-4 py-3" data-testid={`schedule-item-${s.id.slice(0, 8)}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{s.ruangan}</p>
                <p className="text-xs text-muted-foreground">{s.tanggal} · {s.deskripsi || "Maintenance rutin"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant={s.status === "Terealisasi" ? "secondary" : "outline"} data-testid={`schedule-status-${s.id.slice(0, 8)}`}>{s.status}</Badge>
                <Button variant="ghost" size="icon" onClick={() => markDone(s)} title="Tandai terealisasi" data-testid={`schedule-done-${s.id.slice(0, 8)}`}>
                  <CheckCircle2 className={`w-4 h-4 ${s.status === "Terealisasi" ? "text-primary" : ""}`} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setConfirmSched(s)} title="Hapus" data-testid={`schedule-delete-${s.id.slice(0, 8)}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
          {schedules.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="schedules-empty">Belum ada jadwal bulan ini. Klik tanggal pada kalender untuk menambah.</p>}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Jadwal Maintenance</DialogTitle>
            <DialogDescription>Pilih ruangan, tanggal, dan deskripsi pekerjaan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Ruangan</Label>
              <Input list="ruangan-options" value={form.ruangan} onChange={(e) => setForm({ ...form, ruangan: e.target.value })} placeholder="Contoh: IGD, ICU, Radiologi" data-testid="schedule-form-ruangan" />
              <datalist id="ruangan-options">
                {rooms.map((r) => (
                  <option key={r} value={r} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label>Tanggal</Label>
              <Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="schedule-form-tanggal" />
            </div>
            <div className="space-y-2">
              <Label>Deskripsi</Label>
              <Input value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} placeholder="Contoh: Servis rutin bulanan" data-testid="schedule-form-deskripsi" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="schedule-form-cancel">Batal</Button>
            <Button onClick={save} data-testid="schedule-form-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailDay} onOpenChange={(o) => !o && setDetailDay(null)}>
        <DialogContent data-testid="schedule-detail-dialog">
          <DialogHeader>
            <DialogTitle>Jadwal Maintenance — {detailDay}</DialogTitle>
            <DialogDescription>Detail jadwal pada tanggal ini. Lakukan konfirmasi realisasi setelah pekerjaan selesai.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(byDate[detailDay] || []).map((s) => (
              <div key={s.id} className="border border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid={`detail-schedule-${s.id.slice(0, 8)}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{s.ruangan}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.deskripsi || "Maintenance rutin"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={s.status === "Terealisasi" ? "secondary" : "outline"} data-testid={`detail-status-${s.id.slice(0, 8)}`}>{s.status}</Badge>
                  <Button size="sm" variant={s.status === "Terealisasi" ? "outline" : "default"} onClick={() => markDone(s)} data-testid={`konfirmasi-${s.id.slice(0, 8)}`}>
                    {s.status === "Terealisasi" ? "Batal Realisasi" : "Konfirmasi Realisasi"}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setConfirmSched(s)} title="Hapus" data-testid={`detail-delete-${s.id.slice(0, 8)}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { const d = detailDay; setDetailDay(null); openAdd(new Date(d + "T00:00:00")); }} data-testid="detail-add-button">
              <Plus className="w-4 h-4 mr-2" /> Tambah Jadwal di Tanggal Ini
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmSched}
        onOpenChange={(o) => !o && setConfirmSched(null)}
        onConfirm={() => { remove(confirmSched); setConfirmSched(null); }}
        title="Apakah Anda yakin?"
        description={`Jadwal maintenance ruangan "${confirmSched?.ruangan}" tanggal ${confirmSched?.tanggal} akan dihapus.`}
        confirmLabel="Ya, Hapus"
        testid="confirm-delete-schedule"
      />
    </div>
  );
}
